import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { DialogRef, DIALOG_DATA } from '@angular/cdk/dialog'

import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatOptionModule } from '@angular/material/core'

import { firstValueFrom } from 'rxjs'

import { Company } from '../../../../types/company'
import { CompanyService } from '../../../../services/company.service'
import { TranslationService } from '../../../../services/translation.service'
import {
  ConnectionRow,
  ManageConnectionsDialogData,
} from '../../../../types/companyConnection'

@Component({
  selector: 'app-manage-connections-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
    MatAutocompleteModule,
    MatOptionModule,
  ],
  templateUrl: './manage-connections-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./manage-connections-dialog.component.scss'],
})
export class ManageConnectionsDialogComponent implements OnInit {
  data = inject<ManageConnectionsDialogData>(DIALOG_DATA)
  private dialogRef =
    inject<DialogRef<ManageConnectionsDialogComponent>>(DialogRef)
  private companyService = inject(CompanyService)
  translationService = inject(TranslationService)

  // Current connections table
  displayedColumns: string[] = ['name', 'status', 'remove']
  connectionsDataSource = new MatTableDataSource<ConnectionRow>([])

  // Add connection search
  searchName = ''

  suggestions: Company[] = []

  errorMessage: string | null = null

  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null

  async ngOnInit(): Promise<void> {
    this.errorMessage = null
    await this.loadConnections()
  }

  /**
   * Load the current company connections.
   * @returns {Promise<void>} Resolves when the table data has been refreshed.
   */
  async loadConnections(): Promise<void> {
    this.errorMessage = null
    try {
      const res = await firstValueFrom(
        this.companyService.getConnectedCompanies(this.data.companyId),
      )

      // The connections endpoint returns active companies only.
      const rows: ConnectionRow[] = (res ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        is_active: true,
      }))

      this.connectionsDataSource.data = rows
    } catch {
      this.errorMessage = this.translationService.getFailedToLoadCompaniesText()
    }
  }

  /**
   * Trigger autocomplete search when the user types.
   * @param {string} value Current input value.
   * @returns {void}
   */
  onSearchNameChange(value: string): void {
    this.errorMessage = null

    const name = (value ?? '').trim()

    if (name.length < 2) {
      this.suggestions = []
      if (this.searchDebounceHandle) clearTimeout(this.searchDebounceHandle)
      this.searchDebounceHandle = null
      return
    }

    if (this.searchDebounceHandle) clearTimeout(this.searchDebounceHandle)

    this.searchDebounceHandle = setTimeout(() => {
      void this.fetchSuggestions(name)
    }, 250)
  }

  /**
   * Fetch autocomplete company suggestions.
   * @param {string} name Company name query (2+ characters).
   * @returns {Promise<void>} Resolves after suggestions have been updated.
   */
  private async fetchSuggestions(name: string): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.companyService.autocompleteCompanies(name, 10),
      )
      const results = res ?? []

      const existingIds = new Set(
        this.connectionsDataSource.data.map((c) => c.id),
      )

      this.suggestions = results.filter(
        (c) => c.id !== this.data.companyId && !existingIds.has(c.id),
      )
    } catch {
      this.errorMessage = this.translationService.getFailedToLoadCompaniesText()
      this.suggestions = []
    }
  }

  /**
   * Handle autocomplete selection and immediately create the connection.
   * @param {Company} company Selected company.
   * @returns {Promise<void>} Resolves after the connection has been created and the table refreshed.
   */
  async onCompanySelected(company: Company): Promise<void> {
    // Clear the input immediately so the UX feels instant
    this.searchName = ''
    this.suggestions = []

    await this.addConnection(company.id)
  }

  /**
   * Create a mutual company connection.
   * @param {string} targetCompanyId The company to connect to.
   * @returns {Promise<void>} Resolves after the connection has been created and the table refreshed.
   */
  async addConnection(targetCompanyId: string): Promise<void> {
    this.errorMessage = null

    try {
      await firstValueFrom(
        this.companyService.createMutualConnection({
          companyId: this.data.companyId,
          connectedCompanyId: targetCompanyId,
        }),
      )

      await this.loadConnections()
    } catch {
      this.errorMessage =
        this.translationService.getSomethingWentWrongText?.() ??
        'Something went wrong'
    }
  }

  /**
   * Remove an existing company connection.
   * @param {string} targetCompanyId The connected company to remove.
   * @returns {Promise<void>} Resolves after the connection has been removed and the table refreshed.
   */
  async removeConnection(targetCompanyId: string): Promise<void> {
    this.errorMessage = null

    try {
      await firstValueFrom(
        this.companyService.deleteMutualConnection({
          companyId: this.data.companyId,
          connectedCompanyId: targetCompanyId,
        }),
      )

      await this.loadConnections()
    } catch {
      this.errorMessage =
        this.translationService.getSomethingWentWrongText?.() ??
        'Something went wrong'
    }
  }

  /**
   * Close the dialog.
   * @returns {void}
   */
  close(): void {
    this.dialogRef.close()
  }
}
